package com.anjuros.website.redesign.sections.about

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdBreadcrumb
import com.anjuros.website.redesign.components.RdExternalLink
import com.anjuros.website.redesign.components.RdLede
import com.anjuros.website.redesign.components.RdPicture
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.components.RdTag
import com.anjuros.website.redesign.styles.AboutAvatarStyle
import com.anjuros.website.redesign.styles.AboutHeroGridStyle
import com.anjuros.website.redesign.styles.AboutProfileBodyStyle
import com.anjuros.website.redesign.styles.AboutProfileFactsStyle
import com.anjuros.website.redesign.styles.AboutProfileLinksStyle
import com.anjuros.website.redesign.styles.AboutProfileNameStyle
import com.anjuros.website.redesign.styles.AboutProfileStyle
import com.anjuros.website.redesign.styles.AboutProfileTopStyle
import com.anjuros.website.redesign.styles.RdFactsStyle
import com.anjuros.website.redesign.styles.RdGhostButtonStyle
import com.anjuros.website.redesign.styles.RdHeroCopyStyle
import com.anjuros.website.redesign.styles.RdHeroStyle
import com.anjuros.website.redesign.styles.RdMarkStyle
import com.anjuros.website.redesign.styles.RdPrimaryButtonStyle
import com.anjuros.website.redesign.styles.RdSubpageTitleStyle
import com.anjuros.website.redesign.util.RdExternal
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private val Facts = listOf(
    RdText.ABOUT_FACT1_VALUE to RdText.ABOUT_FACT1_LABEL,
    RdText.ABOUT_FACT2_VALUE to RdText.ABOUT_FACT2_LABEL,
    RdText.ABOUT_FACT3_VALUE to RdText.ABOUT_FACT3_LABEL,
)

private val ProfileFacts = listOf(
    RdText.ABOUT_BACKGROUND_LABEL to RdText.ABOUT_BACKGROUND,
    RdText.ABOUT_EDUCATION_LABEL to RdText.ABOUT_EDUCATION,
    RdText.ABOUT_COMPANY_LABEL to RdText.ABOUT_COMPANY,
)

@Composable
fun AboutHeroSection() {
    RdSection(labelledBy = "h1", modifier = RdHeroStyle.toModifier(), wrapModifier = AboutHeroGridStyle.toModifier()) {
        Div(RdHeroCopyStyle.toModifier().toAttrs()) {
            RdBreadcrumb(RdText.ABOUT_CRUMB)
            H1(RdSubpageTitleStyle.toModifier().id("h1").toAttrs()) {
                Text(LocalText(RdText.ABOUT_HERO_TITLE_START))
                Span(RdMarkStyle.toModifier().toAttrs()) { Text(LocalText(RdText.ABOUT_HERO_TITLE_MARK)) }
                Text(LocalText(RdText.ABOUT_HERO_TITLE_END))
            }
            RdLede(RdText.ABOUT_HERO_LEDE)
            Ul(RdFactsStyle.toModifier().toAttrs()) {
                Facts.forEach { (value, label) ->
                    Li {
                        B { Text(LocalText(value)) }
                        Span { Text(LocalText(label)) }
                    }
                }
            }
        }
        ProfileCard()
    }
}

/** Founder card: illustrated avatar, name, key facts and profile links. */
@Composable
private fun ProfileCard() {
    val label = LocalText(RdText.ABOUT_PROFILE_LABEL)
    Aside(AboutProfileStyle.toModifier().attrsModifier { attr("aria-label", label) }.toAttrs()) {
        Div(AboutProfileTopStyle.toModifier().toAttrs()) { BlinkingAvatar() }
        Div(AboutProfileBodyStyle.toModifier().toAttrs()) {
            Div(AboutProfileNameStyle.toModifier().toAttrs()) {
                B { Text(LocalText(RdText.ABOUT_NAME)) }
                Span { Text(LocalText(RdText.ABOUT_ROLE)) }
            }
            RdTag("dl", AboutProfileFactsStyle.toModifier()) {
                ProfileFacts.forEach { (term, value) ->
                    RdTag("dt") { Text(LocalText(term)) }
                    RdTag("dd") { Text(LocalText(value)) }
                }
            }
            Div(AboutProfileLinksStyle.toModifier().toAttrs()) {
                RdExternalLink(RdExternal.LINKEDIN, RdPrimaryButtonStyle.toModifier()) { Text(LocalText(RdText.ABOUT_LINKEDIN)) }
                RdExternalLink(RdExternal.DEVELOPER_PORTFOLIO, RdGhostButtonStyle.toModifier()) { Text(LocalText(RdText.ABOUT_PORTFOLIO)) }
            }
        }
    }
}

/**
 * The avatar closes its eyes while the pointer is on it, and opens them again when it leaves.
 * Both pictures load with the page and [AboutAvatarStyle] swaps them on hover, so there is no flicker.
 */
@Composable
private fun BlinkingAvatar() {
    Div(AboutAvatarStyle.toModifier().toAttrs()) {
        RdPicture(RdImage.AVATAR, LocalText(RdText.ABOUT_AVATAR_ALT), lazy = false)
        RdPicture(RdImage.AVATAR_BLINK, alt = "", lazy = false, modifier = Modifier.attrsModifier { attr("aria-hidden", "true") })
    }
}
